// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
//
import { BufferedRemoteLogMessageSender } from "@lib/logging/BufferedRemoteLogMessageSender";
import { CachingRemoteLogMessageSender } from "@lib/logging/CachingRemoteLogMessageSender";
import { ConsoleLogger } from "@lib/logging/ConsoleLogger";
import { ILog } from "@lib/logging/ILog";
import { IRemoteLogMessageSender } from "@lib/logging/IRemoteLogMessageSender";
import { Log } from "@lib/logging/Log";
import { LogContext } from "@lib/logging/LogContext";
import { LoggingConfiguration } from "@lib/logging/LoggingConfiguration";
import { LoggingConfigurationStorage } from "@lib/logging/LoggingConfigurationStorage";
import { LogLevel } from "@lib/logging/LogLevel";
import { RemoteLogger } from "@lib/logging/RemoteLogger";
import { RootLogger } from "@lib/logging/RootLogger";
import { Constants } from "@lib/utilities/Constants";

const consoleLogger = new ConsoleLogger(LogLevel.Debug);

const bufferedRemoteLogMessageSender = new BufferedRemoteLogMessageSender(Constants.RemoteLogCacheCapacity);
const cachingRemoteLogMessageSender = new CachingRemoteLogMessageSender(bufferedRemoteLogMessageSender);
const remoteLogger = new RemoteLogger(cachingRemoteLogMessageSender, LogLevel.Debug);

/**
 * Provides helper methods for configuring and using logging.
 * By default the console is used as logger.
 * Remote logging must be enabled explicitly.
 */
export class LogManager {
  private static readonly cachedLoggers: Dictionary<ILog> = {};

  private static readonly logContext: LogContext = {
    rootName: "",
    defaultLogLevel: LogLevel.All
  };

  static readonly consoleLogger = consoleLogger;
  static readonly remoteLogger = remoteLogger;

  private static readonly rootLogger = new RootLogger([
    consoleLogger,
    remoteLogger
  ]);

  static get rootName(): string {
    return this.logContext.rootName;
  }

  static set rootName(value: string) {
    this.logContext.rootName = value;
  }

  static get defaultLogLevel(): LogLevel {
    return this.logContext.defaultLogLevel;
  }

  static set defaultLogLevel(value: LogLevel) {
    this.logContext.defaultLogLevel = value;
  }

  static get remoteLogMessageSendingEnabled(): boolean {
    return cachingRemoteLogMessageSender.enabled;
  }

  static set remoteLogMessageSendingEnabled(value: boolean) {
    cachingRemoteLogMessageSender.enabled = value;
  }

  static get remoteLogMessageSender(): Nullable<IRemoteLogMessageSender> {
    return cachingRemoteLogMessageSender.remoteLogMessageSender;
  }

  static set remoteLogMessageSender(value: Nullable<IRemoteLogMessageSender>) {
    cachingRemoteLogMessageSender.remoteLogMessageSender = value;
  }

  static clearPersistedLoggingConfiguration(): void {
    LoggingConfigurationStorage.removeFromLoggingConfiguration(Constants.LoggingConfigurationPersistKey);
  }

  static clearPersistedRemoteLoggingConfiguration(): void {
    LoggingConfigurationStorage.removeFromLoggingConfiguration(Constants.RemoteLoggingConfigurationPersistKey);
  }

  static configureLogging(configuration: LoggingConfiguration, persist: boolean) {
    if (persist) {
      LoggingConfigurationStorage.setLoggingConfiguration(Constants.LoggingConfigurationPersistKey, JSON.stringify(configuration));
    }

    if (typeof configuration.enabled === "boolean")
      consoleLogger.enabled = configuration.enabled;

    if (typeof configuration.logLevel === "number")
      consoleLogger.logLevel = this.parseLogLevel(configuration.logLevel);
  }

  static configureRemoteLogging(configuration: LoggingConfiguration, persist: boolean) {
    if (persist) {
      LoggingConfigurationStorage.setLoggingConfiguration(Constants.RemoteLoggingConfigurationPersistKey, JSON.stringify(configuration));
    }

    if (typeof configuration.enabled === "boolean")
      remoteLogger.enabled = configuration.enabled;

    if (typeof configuration.logLevel === "number")
      remoteLogger.logLevel = this.parseLogLevel(configuration.logLevel);
  }

  static createLog(name: string): ILog {
    const cachedLogger = this.cachedLoggers[name];
    if (cachedLogger)
      return cachedLogger;

    const log = new Log(name, this.rootLogger, this.logContext);
    this.cachedLoggers[name] = log;
    return log;
  }

  private static parseLogLevel(value: number): LogLevel {
    switch (value as LogLevel) {
      case LogLevel.All:
        return LogLevel.All;
      case LogLevel.Debug:
        return LogLevel.Debug;
      case LogLevel.Information:
        return LogLevel.Information;
      case LogLevel.Warning:
        return LogLevel.Warning;
      case LogLevel.Error:
        return LogLevel.Error;
      case LogLevel.Silent:
        return LogLevel.Silent;
    }

    if (value < LogLevel.All)
      return LogLevel.All;
    else
      return LogLevel.Silent;
  }
}

async function InitializeLogging(): Promise<void> {

// Load stored logging configuration and apply it
  try {
    const value = await LoggingConfigurationStorage.getLoggingConfiguration(Constants.LoggingConfigurationPersistKey);

    if (value) {
      const configuration = JSON.parse(value) as LoggingConfiguration;
      if (typeof configuration !== "object")
        throw new Error("Invalid configuration for remote logging found.");

      LogManager.configureLogging(configuration, false);
    }
  } catch (ex) {
    console.warn("Removed invalid logging configuration.");
    await LoggingConfigurationStorage.removeFromLoggingConfiguration(Constants.LoggingConfigurationPersistKey);
  }

// Load stored remote logging configuration and apply it
  try {
    const value = await LoggingConfigurationStorage.getLoggingConfiguration(Constants.RemoteLoggingConfigurationPersistKey);

    if (value) {
      const configuration = JSON.parse(value) as LoggingConfiguration;
      if (typeof configuration !== "object")
        throw new Error("Invalid configuration for local logging found.");

      LogManager.configureRemoteLogging(configuration, false);
    }
  } catch (ex) {
    console.warn("Removed invalid remote logging configuration.");
    await LoggingConfigurationStorage.removeFromLoggingConfiguration(Constants.RemoteLoggingConfigurationPersistKey);
  }
}

InitializeLogging().then(() => {})